import {
  DetailHeader,
  type DetailHeaderAction,
  EmptyState,
  ErrorState,
  LoadingState,
  Markdown,
  READING_MEASURE,
} from "@elyxeer/ui";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import type { EventLinkState } from "~/components/events/event-origin";
import { EventBooking } from "~/components/events/EventBooking";
import { EventQuestions } from "~/components/events/EventQuestions";
import { EventStateBadge } from "~/components/events/EventStateBadge";
import { EventTime } from "~/components/events/EventTime";
import { ArticleCover } from "~/components/news/ArticleCover";
import { ContentDisclaimer } from "~/components/ui/ContentDisclaimer";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { track } from "~/lib/analytics";
import {
  ApiError,
  askEventQuestion,
  fetchEvent,
  fetchEventQuestions,
} from "~/lib/api";
import { addToCalendar } from "~/lib/calendar-export";
import { openExternal } from "~/lib/external-link";
import { queryKeys } from "~/lib/query-client";
import { useFireOnce } from "~/lib/use-fire-once";
import { useAuthStore } from "~/store/auth-store";
import { AnalyticsEventName } from "~/types/analytics";
import type { EventDetail } from "~/types/event";

/**
 * Whether the legacy outbound link still earns a button.
 *
 * It was the whole screen once — "register somewhere else", "watch the stream there".
 * Booking now happens in the app and the broadcast link is gated by the state, so the
 * button survives only where neither replaces it: a registration link on an event that
 * takes no in-app bookings, or a stream link the editor has not yet moved over.
 */
function showsLegacyLink(event: EventDetail): boolean {
  if (!event.external_url) return false;
  if (event.external_kind === "register") return !event.registration_enabled;
  return !event.stream_url;
}

/**
 * One event.
 *
 * The booking block is the point of the screen: what the member can do about this
 * event right now — book a place, hold it, join the broadcast, play the recording. The
 * state it reads comes from the server with the event. Putting the event into the
 * member's own calendar is secondary, so it sits in the header's overflow menu as well
 * as on the booked card. Questions go to the moderator's list, never to the chat.
 */
export function EventRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { slug = "" } = useParams<{ slug: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id ?? null);

  // The two entry points are different tabs, so the way back follows where the member
  // actually came from rather than always landing on the calendar. No origin — a shared
  // link or a refresh — lands on the dashboard.
  const cameFromCalendar =
    (location.state as EventLinkState | null)?.from === "calendar";

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKeys.event(userId ?? "", locale, slug),
    queryFn: ({ signal }) => fetchEvent(slug, locale, signal),
    enabled: Boolean(userId) && slug.length > 0,
    // The state follows the clock; a screen left open across the start should notice.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const questions = useQuery({
    queryKey: queryKeys.eventQuestions(userId ?? "", slug),
    queryFn: ({ signal }) => fetchEventQuestions(slug, signal),
    enabled: Boolean(userId) && slug.length > 0,
  });

  const ask = useMutation({
    mutationFn: (body: string) => askEventQuestion(slug, locale, body),
    onSuccess: async () => {
      track(AnalyticsEventName.EventQuestionAsked, {
        subjectType: "event",
        subjectId: data?.id ?? null,
        subjectKey: slug,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.eventQuestions(userId ?? "", slug),
      });
    },
  });

  // Keyed on the resolved id, so neither the 404 nor the error branch counts as a view.
  useFireOnce(data?.id ?? null, () => {
    track(AnalyticsEventName.EventView, {
      subjectType: "event",
      subjectId: data?.id ?? null,
      subjectKey: data?.slug ?? null,
      // The route already knows which tab the member came from; recording it costs
      // nothing and answers whether the calendar or the dashboard drives attendance.
      props: { referrer: cameFromCalendar ? "calendar" : "dashboard" },
    });
  });

  // Only once the event is loaded: a menu with nothing to act on is a broken promise.
  const actions = useMemo(
    (): DetailHeaderAction[] =>
      data
        ? [
            {
              id: "add-to-calendar",
              label: t("events.addToCalendar"),
              icon: <EventAvailableOutlinedIcon fontSize="small" />,
              onSelect: () => {
                track(AnalyticsEventName.EventAddToCalendar, {
                  subjectType: "event",
                  subjectId: data.id,
                  subjectKey: data.slug,
                });
                void addToCalendar({ ...data, url: data.stream_url });
              },
            },
          ]
        : [],
    [data, t],
  );

  const backLink = (
    <DetailHeader
      backTo={cameFromCalendar ? "/events" : "/news"}
      backLabel={t(
        cameFromCalendar ? "events.backToCalendar" : "events.backToDashboard",
      )}
      backTestId="event.back"
      actions={actions}
      menuLabel={t("common.moreActions")}
      menuTestId="event.menu"
    />
  );

  if (isPending) {
    return (
      <Screen maxWidth="md">
        {backLink}
        <LoadingState />
      </Screen>
    );
  }

  // A missing event is an ordinary outcome — a stale link, or a slug that only exists
  // in the other language — not an error worth a retry button.
  if (isError && error instanceof ApiError && error.status === 404) {
    return (
      <Screen maxWidth="md">
        {backLink}
        <EmptyState message={t("events.notFound")} />
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen maxWidth="md">
        {backLink}
        <ErrorState
          message={error instanceof Error ? error.message : undefined}
          onRetry={() => void refetch()}
        />
      </Screen>
    );
  }

  return (
    <Screen maxWidth="md">
      <Box
        component="article"
        sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 } }}
      >
        {backLink}

        {data.cover_image_url ? (
          <ArticleCover slug={data.slug} src={data.cover_image_url} alt="" />
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxWidth: READING_MEASURE,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <EventStateBadge state={data.state} always />
            <EventTime event={data} withDate />
          </Box>

          <Typography
            variant="H1"
            sx={{ color: "text.primary" }}
            data-testid="event.title"
          >
            {data.title}
          </Typography>

          {data.summary ? (
            <Typography
              component="p"
              variant="H3"
              sx={{ color: "text.secondary" }}
            >
              {data.summary}
            </Typography>
          ) : null}
        </Box>

        {userId ? (
          <EventBooking event={data} locale={locale} userId={userId} />
        ) : null}

        {showsLegacyLink(data) ? (
          <Button
            variant={data.registration_enabled ? "outlined" : "contained"}
            size="large"
            endIcon={<OpenInNewIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => {
              // Registration *intent*: the link leaves the app for a third-party
              // platform, so this is the last thing about it we can observe.
              track(AnalyticsEventName.EventCtaClick, {
                subjectType: "event",
                subjectId: data.id,
                subjectKey: data.slug,
                props: { kind: data.external_kind ?? "unknown" },
              });
              void openExternal(data.external_url!);
            }}
            data-testid="event.external-link"
            sx={{ alignSelf: "flex-start" }}
          >
            {t(
              data.external_kind === "register"
                ? "events.register"
                : "events.watchStream",
            )}
          </Button>
        ) : null}

        <Box sx={{ maxWidth: READING_MEASURE }}>
          <Markdown>{data.body}</Markdown>
        </Box>

        {userId ? (
          <Box sx={{ maxWidth: READING_MEASURE }}>
            <EventQuestions
              questions={questions.data ?? []}
              open={data.questions_open}
              onAsk={(body) => ask.mutateAsync(body)}
            />
          </Box>
        ) : null}

        <ContentDisclaimer />
      </Box>
    </Screen>
  );
}
